// @ts-nocheck - formatter / Table
import React, { useState } from 'react';
import { NavLink, useLocation } from 'react-router-dom';
import {
  loadAccountDetails,
  exportAccountData,
} from '../../../actions/accountDetailActions';
import { Table, SimpleTextFilter, getVisibleColumns } from '../../common/Table';
import Loading from '../../common/Loading';
import KwPanel from '../../common/KwPanel';
import {
  useTableSettings,
  getTableSettingsDefaults,
} from '../../../hooks/useTableSettings';
import { CommaNumber_NoDecimal } from '../../../util/formatter';
import KeyProviderLink from '../../common/links/KeyProviderLink';
import { useTranslation } from 'react-i18next';

export interface AccountsListProps {
  currentUser?: any;
  reload?: any;
  setBreadcrumb: (crumb: any) => void;
  tableViewSwitcher?: React.ReactNode;
  itemsChecked?: any[];
}

function AccountsList({
  reload,
  setBreadcrumb,
  tableViewSwitcher,
}: AccountsListProps) {
  const location = useLocation();
  const { t } = useTranslation();
  const allColumns = [
    'login',
    'name',
    'emailAddress',
    'keyProvider',
    'keysAllocated',
    'keysDownloaded',
    'keysDisabled',
  ];
  const defaultVisibleColumns = allColumns;
  const [accountDetails, setAccountDetails] = useState<any>();
  const params = new URLSearchParams(location.search);

  const { tableSettings, handleSetTableSettings: setTableSettings, filters, filterChange, isLoading } =
    useTableSettings(
      'accountDetails-settings',
      getTableSettingsDefaults({
        defaultSort: 'login',
        direction: 'desc',
        defaultVisibleColumns,
      }),
      null,
      loadAccountDetails,
      setAccountDetails,
      reload,
      t('Failed to load account details')
    );

  const columnsConfig = [
    {
      Key: 'login',
      Label: t('Login'),
      width: '20%',
      Render: (item: any) => (
        <NavLink to={`/keys/account/${item.id}`}>{item.login}</NavLink>
      ),
      Filter: {
        Render: (col: any) => (
          <SimpleTextFilter
            Key={col.Key}
            Value={col.filterValue ?? params.get(`f_${col.Key}`) ?? ''}
            onChange={(value) => filterChange(col.Key, value)}
          />
        ),
      },
    },
    {
      Key: 'name',
      Label: t('Name'),
      width: '20%',
      Filter: {
        Render: (col: any) => (
          <SimpleTextFilter
            Key={col.Key}
            Value={col.filterValue ?? params.get(`f_${col.Key}`) ?? ''}
            onChange={(value) => filterChange(col.Key, value)}
          />
        ),
      },
    },
    {
      Key: 'emailAddress',
      Label: t('Email address'),
      width: '20%',
      Filter: {
        Render: (col: any) => (
          <SimpleTextFilter
            Key={col.Key}
            Value={col.filterValue ?? params.get(`f_${col.Key}`) ?? ''}
            onChange={(value) => filterChange(col.Key, value)}
          />
        ),
      },
    },
    {
      Key: 'keyProvider',
      Label: t('Key provider'),
      width: '19%',
      Render: (item: any) => (
        <KeyProviderLink id={item.keyProviderId} name={item.keyProvider} />
      ),
      Filter: {
        Render: (col: any) => (
          <SimpleTextFilter
            Key={col.Key}
            Value={col.filterValue ?? params.get(`f_${col.Key}`) ?? ''}
            onChange={(value) => filterChange(col.Key, value)}
          />
        ),
      },
    },
    {
      Key: 'keysAllocated',
      Label: t('Keys allocated'),
      className: 'text-right num no-wrap',
      width: '7%',
      Render: (item: any) =>
        item.keysAllocated >= 0
          ? CommaNumber_NoDecimal(item.keysAllocated)
          : '--',
    },
    {
      Key: 'keysDownloaded',
      Label: t('Keys downloaded'),
      className: 'text-right num no-wrap',
      width: '7%',
      Render: (item: any) =>
        item.keysDownloaded >= 0
          ? CommaNumber_NoDecimal(item.keysDownloaded)
          : '--',
    },
    {
      Key: 'keysDisabled',
      Label: t('Keys disabled'),
      className: 'text-right num no-wrap',
      width: '7%',
      Render: (item: any) =>
        item.keysDisabled >= 0
          ? CommaNumber_NoDecimal(item.keysDisabled)
          : '--',
    },
  ];

  const visibleColumns = tableSettings.columns ?? defaultVisibleColumns;
  const columns = getVisibleColumns(visibleColumns, columnsConfig);

  return accountDetails ? (
    <KwPanel className="mb-4">
      <header>
        <h2 className="mt-3 mb-2">{t('Accounts - Grouped')}</h2>
      </header>
      <Table
        columns={columns}
        allColumns={allColumns}
        rows={accountDetails.data.map((x: any) => ({ Key: x.id, ...x }))}
        totalRowCount={accountDetails.totalRowCount}
        filteredRowCount={accountDetails.filteredRowCount}
        scopes={accountDetails.scopes}
        tableSettings={tableSettings}
        setTableSettings={setTableSettings}
        isLoading={isLoading}
        filters={filters}
        filterChange={filterChange}
        exportData={exportAccountData}
        setBreadcrumb={setBreadcrumb}
        tableActionBarButtons={tableViewSwitcher}
      />
    </KwPanel>
  ) : (
    <Loading />
  );
}

export default AccountsList;
